import {
  Column,
  ColumnContent,
  Empty,
  Error,
  GridForm,
  type GroupImperativeHandle,
  GutterSeparator,
  KeepMountedPanel,
  Panel,
  PanelGroup,
  PanelHandle,
  SetMetadata,
} from "@qw/ui";
import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { ChatColumn } from "@/chat/chat-column";
import { ChatPosition } from "@/providers/page-provider";
import {
  areLayoutsEqual,
  buildInboxShellLayout,
  getContainerWidth,
  getContentMinWidthPx,
  getInboxShellSidePanelWidthsFromLayout,
  hasLayoutForPanelIds,
  INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX,
  INBOX_SIDE_PANEL_MAX_WIDTHS_PX,
  INBOX_SIDE_PANEL_MIN_WIDTHS_PX,
  type InboxShellPanelId,
  mergeInboxSidePanelWidths,
  readInboxSidePanelWidths,
  useElementWidth,
  writeInboxSidePanelWidths,
} from "../inbox/inbox-shell-layout";
import { useBook } from "../providers/use-book";
import { useDrafts } from "../providers/use-drafts";
import { usePage } from "../providers/use-page";
import { LeftSidebar } from "../sidebar/left-sidebar";
import { DraftLink } from "./draft-link";
import { DraftsSelectionHeader } from "./drafts-selection-header";

export const Drafts = () => {
  const { insuredId } = useParams();
  const { isBookOpen } = useBook();
  const { chatPosition, isChatOpen } = usePage();
  const { drafts, error, loading: isLoading } = useDrafts({ insuredId });
  const isSideChatVisible = chatPosition === ChatPosition.side && isChatOpen;
  const chatPanelId: InboxShellPanelId = isSideChatVisible ? "chat" : "chat-mount";
  const contentMinWidthPx = getContentMinWidthPx(false);
  const shellGroupRef = useRef<GroupImperativeHandle | null>(null);
  const shellContainerRef = useRef<HTMLDivElement | null>(null);
  const programmaticShellLayoutsRef = useRef<Record<string, number>[]>([]);
  const [shellContainerElement, setShellContainerElement] = useState<HTMLDivElement | null>(null);
  const [sidePanelWidths, setSidePanelWidths] = useState(readInboxSidePanelWidths);
  const shellContainerWidth = useElementWidth(shellContainerElement);

  const panelIds = useMemo<InboxShellPanelId[]>(() => {
    const panelIds: InboxShellPanelId[] = [];
    if (isBookOpen) {
      panelIds.push("book");
    }
    panelIds.push("content", chatPanelId);
    return panelIds;
  }, [chatPanelId, isBookOpen]);

  const defaultLayout = useMemo(
    () =>
      buildInboxShellLayout({
        panelIds,
        sidePanelWidths,
        containerWidthPx: shellContainerWidth,
        contentMinWidthPx,
        sidePanelMaxWidthsPx: INBOX_SIDE_PANEL_MAX_WIDTHS_PX,
      }),
    [contentMinWidthPx, panelIds, shellContainerWidth, sidePanelWidths]
  );

  const setProgrammaticShellLayout = useCallback((group: GroupImperativeHandle, nextLayout: Record<string, number>) => {
    programmaticShellLayoutsRef.current = [...programmaticShellLayoutsRef.current, nextLayout].slice(-4);
    group.setLayout(nextLayout);
  }, []);

  useLayoutEffect(() => {
    const group = shellGroupRef.current;

    if (!group || shellContainerWidth <= 0) {
      return;
    }

    const nextLayout = buildInboxShellLayout({
      panelIds,
      sidePanelWidths,
      containerWidthPx: shellContainerWidth,
      contentMinWidthPx,
      sidePanelMaxWidthsPx: INBOX_SIDE_PANEL_MAX_WIDTHS_PX,
    });

    const currentLayout = group.getLayout();

    if (!hasLayoutForPanelIds(currentLayout, panelIds)) {
      return;
    }

    if (!areLayoutsEqual(currentLayout, nextLayout)) {
      setProgrammaticShellLayout(group, nextLayout);
    }
  }, [contentMinWidthPx, panelIds, setProgrammaticShellLayout, shellContainerWidth, sidePanelWidths]);

  const setShellContainerRef = useCallback((element: HTMLDivElement | null) => {
    shellContainerRef.current = element;
    setShellContainerElement(element);
  }, []);

  const handleShellLayoutChanged = useCallback(
    (layout: Record<string, number>) => {
      const programmaticLayoutIndex = programmaticShellLayoutsRef.current.findIndex((programmaticLayout) =>
        areLayoutsEqual(layout, programmaticLayout)
      );

      if (programmaticLayoutIndex >= 0) {
        programmaticShellLayoutsRef.current = programmaticShellLayoutsRef.current.slice(programmaticLayoutIndex + 1);
        return;
      }

      if (programmaticShellLayoutsRef.current.length > 0) {
        programmaticShellLayoutsRef.current = [];
      }

      const containerWidthPx = getContainerWidth(shellContainerRef.current);
      const nextWidths = getInboxShellSidePanelWidthsFromLayout({ panelIds, layout, containerWidthPx });
      const builtWidths = getInboxShellSidePanelWidthsFromLayout({ panelIds, layout: defaultLayout, containerWidthPx });

      setSidePanelWidths((currentWidths) => {
        const mergedWidths = mergeInboxSidePanelWidths(currentWidths, nextWidths, [builtWidths]);

        if (mergedWidths !== currentWidths) {
          writeInboxSidePanelWidths(mergedWidths);
        }

        return mergedWidths;
      });
    },
    [defaultLayout, panelIds]
  );

  if (error) {
    return <Error error={error} className="p-4 sm:p-6 md:p-8" />;
  }

  return (
    <>
      <SetMetadata title="Drafts" />
      <div ref={setShellContainerRef} className="h-full w-full">
        <PanelGroup
          direction="horizontal"
          defaultLayout={defaultLayout}
          groupRef={shellGroupRef}
          onLayoutChanged={handleShellLayoutChanged}
          panelIds={panelIds}
        >
          {isBookOpen && (
            <Fragment key="book-slot">
              <Panel
                key="book"
                id="book"
                minSize={`${INBOX_SIDE_PANEL_MIN_WIDTHS_PX.book}px`}
                maxSize={`${INBOX_SIDE_PANEL_MAX_WIDTHS_PX.book}px`}
                defaultSize={`${INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.book}px`}
                className="overflow-hidden"
              >
                <LeftSidebar rootPath="/drafts" withBorder={false} />
              </Panel>
              <PanelHandle key="book-handle" />
            </Fragment>
          )}
          <Panel
            key="content"
            id="content"
            minSize={`${contentMinWidthPx}px`}
            defaultSize={`${contentMinWidthPx}px`}
            className="flex min-h-0 min-w-0 overflow-hidden"
          >
            <GridForm data={drafts} className="flex h-full min-w-0 overflow-hidden">
              <Column className="bg-background">
                <DraftsSelectionHeader loading={isLoading} />
                <ColumnContent>
                  {!isLoading && drafts.length === 0 ? (
                    <Empty
                      icon="edit_document"
                      title="No drafts found"
                      description="You don't have any saved drafts."
                    />
                  ) : (
                    drafts.map((draft) => (
                      <Fragment key={draft.id}>
                        <DraftLink draft={draft} />
                        <GutterSeparator />
                      </Fragment>
                    ))
                  )}
                </ColumnContent>
              </Column>
            </GridForm>
          </Panel>
          <PanelHandle key="chat-handle" hidden={!isSideChatVisible} />
          <KeepMountedPanel
            key="chat-slot"
            id="chat"
            hidden={!isSideChatVisible}
            minSize={`${INBOX_SIDE_PANEL_MIN_WIDTHS_PX.chat}px`}
            maxSize={`${INBOX_SIDE_PANEL_MAX_WIDTHS_PX.chat}px`}
            defaultSize={`${INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.chat}px`}
            className="overflow-hidden"
          >
            <ChatColumn layout={isSideChatVisible ? "panel" : "flex"} mountOnly={!isChatOpen} />
          </KeepMountedPanel>
        </PanelGroup>
      </div>
    </>
  );
};
